(() => {
  'use strict';

  const config = window.HPDTheme || {};
  const routes = config.routes || {};

  function formatMoney(minorUnits) {
    const value = Number(minorUnits) || 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: config.currency || 'GBP',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value / 100);
    } catch (error) {
      return `£${(value / 100).toFixed(2)}`;
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function safeUrl(value, fallback = '#') {
    if (!value) return fallback;
    try {
      const parsed = new URL(value, window.location.origin);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return parsed.href;
      }
    } catch (error) {
      return fallback;
    }
    return fallback;
  }

  function debounce(callback, wait = 250) {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  }

  function calculateVatTotals(cart) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    const lineItemsTotal = items.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    const taxableItemsTotal = items.reduce(
      (sum, item) => sum + (item.taxable ? Number(item.final_line_price || 0) : 0),
      0
    );
    const cartTotal = Number(cart.total_price || 0);
    const cartLevelDiscount = Math.max(0, lineItemsTotal - cartTotal);
    let taxableAfterDiscounts = taxableItemsTotal;

    if (lineItemsTotal > 0 && cartLevelDiscount > 0 && taxableItemsTotal > 0) {
      taxableAfterDiscounts = taxableItemsTotal === lineItemsTotal
        ? cartTotal
        : Math.max(0, taxableItemsTotal - Math.round(cartLevelDiscount * taxableItemsTotal / lineItemsTotal));
    }

    if (config.taxesIncluded) {
      const taxableExVat = Math.round(taxableAfterDiscounts / 1.2);
      const vat = taxableAfterDiscounts - taxableExVat;
      return {
        subtotalExVat: cartTotal - vat,
        vat,
        totalIncVat: cartTotal,
        hasNonTaxableItems: items.some((item) => !item.taxable)
      };
    }

    const vat = Math.round(taxableAfterDiscounts * 0.2);
    return {
      subtotalExVat: cartTotal,
      vat,
      totalIncVat: cartTotal + vat,
      hasNonTaxableItems: items.some((item) => !item.taxable)
    };
  }

  class HPDCartDrawer {
    constructor(shell) {
      this.shell = shell;
      this.drawer = shell.querySelector('[data-hpd-cart-drawer]');
      this.body = shell.querySelector('[data-hpd-cart-body]');
      this.items = shell.querySelector('[data-hpd-cart-items]');
      this.empty = shell.querySelector('[data-hpd-cart-empty]');
      this.footer = shell.querySelector('[data-hpd-cart-footer]');
      this.count = shell.querySelector('[data-hpd-cart-count]');
      this.status = shell.querySelector('[data-hpd-cart-status]');
      this.delivery = shell.querySelector('[data-hpd-cart-delivery]');
      this.clearButton = shell.querySelector('[data-hpd-cart-clear]');
      this.recommendations = shell.querySelector('[data-hpd-cart-recommendations]');
      this.recommendationsList = shell.querySelector('[data-hpd-cart-recommendations-list]');
      this.opener = null;
      this.cart = null;
      this.isOpen = false;
      this.isEmittingCount = false;
      this.lastKnownCount = Number(config.initialCartCount || 0);
      this.pendingThemeFlyout = false;
      this.recommendationsProductId = null;
      this.shell.inert = true;
      this.bindEvents();
    }

    bindEvents() {
      this.shell.addEventListener('click', (event) => {
        const clear = event.target.closest('[data-hpd-cart-clear]');
        if (clear) {
          event.preventDefault();
          this.clearCart();
          return;
        }

        const close = event.target.closest('[data-hpd-cart-close]');
        if (close) {
          event.preventDefault();
          this.close();
          return;
        }

        const action = event.target.closest('[data-hpd-cart-action]');
        if (!action) return;
        event.preventDefault();
        this.handleItemAction(action);
      });

      this.shell.addEventListener('change', (event) => {
        const input = event.target.closest('[data-hpd-cart-quantity]');
        if (!input) return;
        this.changeQuantity(input.dataset.key, Math.max(0, Number.parseInt(input.value, 10) || 0));
      });

      document.addEventListener('click', (event) => {
        const cartLink = event.target.closest('.site-header-cart--button');
        if (!cartLink || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(cartLink);
      }, true);

      document.addEventListener('keydown', (event) => this.handleKeydown(event));

      document.addEventListener('addToCartFlyout:complete', () => {
        this.pendingThemeFlyout = true;
        window.setTimeout(() => {
          if (!this.pendingThemeFlyout) return;
          this.pendingThemeFlyout = false;
          this.open(document.activeElement);
        }, 420);
      });

      window.addEventListener('cartcount:update', (event) => {
        if (!event.detail || this.isEmittingCount) return;
        if (Array.isArray(event.detail.items)) this.renderFullCartTotals(event.detail);
        const nextCount = Number(event.detail.item_count || 0);
        const itemWasAdded = nextCount > this.lastKnownCount;
        this.lastKnownCount = nextCount;
        this.updateHeaderCount(nextCount);

        if (itemWasAdded && !this.pendingThemeFlyout && !document.body.classList.contains('template-cart')) {
          window.setTimeout(() => this.open(document.activeElement), 80);
        } else if (this.isOpen) {
          this.refresh(true);
        }
      });

      this.flyoutObserver = new MutationObserver(() => {
        if (!this.pendingThemeFlyout) return;
        const flyout = document.querySelector('.site-header > [data-atc-banner]');
        if (!flyout) return;
        this.pendingThemeFlyout = false;
        document.dispatchEvent(new Event('closeFlyouts'));
        window.setTimeout(() => this.open(document.activeElement), 100);
      });
      this.flyoutObserver.observe(document.body, { childList: true, subtree: true });
    }

    async open(opener) {
      this.opener = opener instanceof HTMLElement ? opener : document.activeElement;
      if (!this.isOpen) {
        this.isOpen = true;
        this.shell.inert = false;
        this.shell.classList.add('is-open');
        this.shell.setAttribute('aria-hidden', 'false');
        document.body.classList.add('hpd-overlay-open');
        window.requestAnimationFrame(() => this.drawer.focus({ preventScroll: true }));
      }

      await this.refresh(true);
      window.setTimeout(() => this.refresh(true), 800);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (this.opener && document.contains(this.opener)) {
        this.opener.focus({ preventScroll: true });
      }
      this.shell.classList.remove('is-open');
      this.shell.setAttribute('aria-hidden', 'true');
      this.shell.inert = true;
      document.body.classList.remove('hpd-overlay-open');
    }

    handleKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async refresh(quiet = false) {
      try {
        if (!quiet) this.setStatus('Updating cart…');
        const response = await fetch(`${routes.cart || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Unable to load your cart.');
        const cart = await response.json();
        this.render(cart);
        if (!quiet) this.setStatus('Cart updated.');
      } catch (error) {
        this.setStatus(error.message || 'Unable to update your cart.');
      }
    }

    render(cart) {
      this.cart = cart;
      const hasItems = Number(cart.item_count || 0) > 0;
      this.lastKnownCount = Number(cart.item_count || 0);
      this.count.textContent = `(${this.lastKnownCount})`;
      this.empty.classList.toggle('is-hidden', hasItems);
      this.items.classList.toggle('is-hidden', !hasItems);
      this.footer.classList.toggle('is-hidden', !hasItems);
      this.clearButton?.classList.toggle('is-hidden', !hasItems);
      const hasRecommendationCards = this.recommendationsList.children.length > 0;
      this.recommendations.classList.toggle('is-hidden', !hasItems || !hasRecommendationCards);
      this.renderDelivery(cart);
      this.renderItems(cart);
      this.renderTotals(cart);
      this.updateHeaderCount(this.lastKnownCount);
      this.emitCartCount(cart);
      this.loadRecommendations(cart);
    }

    renderDelivery(cart) {
      const threshold = Number(config.freeDeliveryThreshold || 19900);
      const total = Number(cart.total_price || 0);
      const remaining = Math.max(0, threshold - total);
      const progress = Math.max(0, Math.min(100, total / threshold * 100));
      const message = this.delivery.querySelector('[data-hpd-cart-delivery-message]');
      const track = this.delivery.querySelector('[data-hpd-cart-delivery-track]');
      const bar = this.delivery.querySelector('[data-hpd-cart-delivery-bar]');

      message.innerHTML = remaining > 0
        ? `Spend <strong>${formatMoney(remaining)}</strong> more for free UK mainland delivery.`
        : '<strong>You’ve reached the free-delivery threshold.</strong>';
      track.setAttribute('aria-valuenow', String(Math.round(progress)));
      bar.style.width = `${progress}%`;
    }

    renderItems(cart) {
      const items = Array.isArray(cart.items) ? cart.items : [];
      this.items.innerHTML = items.map((item) => {
        const linePrice = Number(item.final_line_price || 0);
        const lineExVat = item.taxable && config.taxesIncluded ? Math.round(linePrice / 1.2) : linePrice;
        const lineIncVat = item.taxable && !config.taxesIncluded ? Math.round(linePrice * 1.2) : linePrice;
        const image = item.featured_image?.url || item.image;
        const properties = Object.entries(item.properties || {})
          .filter(([key, value]) => value && !key.startsWith('_'))
          .map(([key, value]) => `<p class="hpd-cart-item__property"><strong>${escapeHtml(key)}:</strong> ${escapeHtml(value)}</p>`)
          .join('');
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="hpd-cart-item__variant">${escapeHtml(item.variant_title)}</p>`
          : '';
        const vatLine = item.taxable
          ? `<span>${formatMoney(lineIncVat)} inc VAT</span>`
          : '<span>No VAT charged</span>';

        return `
          <li class="hpd-cart-item" data-hpd-cart-item data-key="${escapeHtml(item.key)}">
            <a class="hpd-cart-item__image" href="${safeUrl(item.url, routes.cart || '/cart')}">
              ${image
                ? `<img src="${safeUrl(image)}" alt="${escapeHtml(item.product_title || item.title)}" loading="lazy" width="180" height="180">`
                : '<span aria-hidden="true">Image unavailable</span>'}
            </a>
            <div class="hpd-cart-item__content">
              <a class="hpd-cart-item__title" href="${safeUrl(item.url, routes.cart || '/cart')}">${escapeHtml(item.product_title || item.title)}</a>
              ${variant}
              ${properties}
              <div class="hpd-cart-item__pricing">
                <strong>${formatMoney(lineExVat)} <small>ex VAT</small></strong>
                ${vatLine}
              </div>
              <div class="hpd-cart-item__actions">
                <div class="hpd-cart-item__quantity" aria-label="Quantity for ${escapeHtml(item.product_title || item.title)}">
                  <button type="button" data-hpd-cart-action="decrease" data-key="${escapeHtml(item.key)}" aria-label="Decrease quantity">−</button>
                  <input type="number" min="0" value="${Number(item.quantity || 0)}" inputmode="numeric" data-hpd-cart-quantity data-key="${escapeHtml(item.key)}" aria-label="Quantity">
                  <button type="button" data-hpd-cart-action="increase" data-key="${escapeHtml(item.key)}" aria-label="Increase quantity">+</button>
                </div>
                <button class="hpd-cart-item__remove" type="button" data-hpd-cart-action="remove" data-key="${escapeHtml(item.key)}">Remove</button>
              </div>
            </div>
          </li>`;
      }).join('');
    }

    renderTotals(cart) {
      const totals = calculateVatTotals(cart);
      const totalsElement = this.footer.querySelector('[data-hpd-cart-vat-totals]');
      const note = totals.hasNonTaxableItems
        ? 'Delivery calculated at checkout. Non-taxable items are excluded from the VAT calculation.'
        : 'Delivery calculated at checkout.';

      if (totalsElement) {
        totalsElement.innerHTML = `
          <div class="hpd-cart-vat-totals__row"><span>Subtotal ex VAT</span><span>${formatMoney(totals.subtotalExVat)}</span></div>
          <div class="hpd-cart-vat-totals__row"><span>VAT on goods (20%)</span><span>${formatMoney(totals.vat)}</span></div>
          <div class="hpd-cart-vat-totals__row hpd-cart-vat-totals__row--total"><strong>Total inc VAT</strong><strong>${formatMoney(totals.totalIncVat)}</strong></div>
          <p class="hpd-cart-vat-totals__note">${escapeHtml(note)}</p>`;
      }

      const checkoutTotal = this.footer.querySelector('[data-hpd-cart-checkout-total]');
      if (checkoutTotal) checkoutTotal.textContent = formatMoney(totals.totalIncVat);

      this.renderFullCartTotals(cart);
    }

    renderFullCartTotals(cart) {
      const totals = calculateVatTotals(cart);
      const note = totals.hasNonTaxableItems
        ? 'Delivery calculated at checkout. Non-taxable items are excluded from the VAT calculation.'
        : 'Delivery calculated at checkout.';

      document.querySelectorAll('[data-hpd-full-cart-total]').forEach((element) => {
        element.textContent = formatMoney(totals.totalIncVat);
      });
      document.querySelectorAll('[data-hpd-full-cart-totals]').forEach((element) => {
        element.innerHTML = `
          <div class="hpd-cart-vat-totals" data-hpd-cart-vat-totals>
            <div class="hpd-cart-vat-totals__row"><span>Subtotal ex VAT</span><span>${formatMoney(totals.subtotalExVat)}</span></div>
            <div class="hpd-cart-vat-totals__row"><span>VAT on goods (20%)</span><span>${formatMoney(totals.vat)}</span></div>
            <div class="hpd-cart-vat-totals__row hpd-cart-vat-totals__row--total"><strong>Total inc VAT</strong><strong>${formatMoney(totals.totalIncVat)}</strong></div>
            <p class="hpd-cart-vat-totals__note">${escapeHtml(note)}</p>
          </div>`;
      });
    }

    handleItemAction(action) {
      const key = action.dataset.key;
      const item = this.cart?.items?.find((cartItem) => cartItem.key === key);
      if (!item) return;
      const type = action.dataset.hpdCartAction;
      let quantity = Number(item.quantity || 0);
      if (type === 'increase') quantity += 1;
      if (type === 'decrease') quantity = Math.max(0, quantity - 1);
      if (type === 'remove') quantity = 0;
      this.changeQuantity(key, quantity);
    }

    async changeQuantity(key, quantity) {
      const line = Array.from(this.items.querySelectorAll('[data-hpd-cart-item]'))
        .find((element) => element.dataset.key === key);
      if (line) line.classList.add('is-updating');
      this.setStatus('Updating cart…');

      try {
        const response = await fetch(`${routes.cartChange || '/cart/change'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to update this item.');
        this.render(data);
        this.setStatus(quantity === 0 ? 'Item removed from cart.' : 'Quantity updated.');
      } catch (error) {
        if (line) line.classList.remove('is-updating');
        this.setStatus(error.message || 'Unable to update this item.');
      }
    }

    async clearCart() {
      if (!Number(this.cart?.item_count || 0)) return;
      if (!window.confirm('Remove all items from your cart?')) return;

      this.clearButton.disabled = true;
      this.setStatus('Removing all items…');
      try {
        const response = await fetch(`${routes.cartClear || '/cart/clear'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to clear the cart.');
        this.render(data);
        this.setStatus('All items removed from cart.');
      } catch (error) {
        this.setStatus(error.message || 'Unable to clear the cart.');
      } finally {
        this.clearButton.disabled = false;
      }
    }

    async loadRecommendations(cart) {
      const firstItem = cart.items?.[0];
      if (!firstItem || !routes.productRecommendations) {
        this.recommendations.classList.add('is-hidden');
        return;
      }
      if (this.recommendationsProductId === firstItem.product_id) return;
      this.recommendationsList.innerHTML = '';
      this.recommendations.classList.add('is-hidden');
      this.recommendationsProductId = firstItem.product_id;

      try {
        const url = `${routes.productRecommendations}.json?product_id=${encodeURIComponent(firstItem.product_id)}&limit=4&intent=complementary`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('No recommendations');
        const payload = await response.json();
        const inCart = new Set(cart.items.map((item) => Number(item.product_id)));
        const products = (payload.products || []).filter((product) => {
          const isNotInCart = !inCart.has(Number(product.id));
          const matchesBrand = !window.HPDBrandMode || window.HPDBrandMode.brandMatchesVendor(product.vendor);
          return isNotInCart && matchesBrand;
        }).slice(0, 3);
        if (!products.length) {
          this.recommendations.classList.add('is-hidden');
          return;
        }

        this.recommendationsList.innerHTML = `<div class="hpd-cart-recommendations__grid">${products.map((product) => {
          const image = product.featured_image || product.images?.[0];
          const price = Number(product.price || product.variants?.[0]?.price || 0);
          const taxable = product.variants?.[0]?.taxable !== false;
          const incPrice = taxable && !config.taxesIncluded ? Math.round(price * 1.2) : price;
          return `
            <a class="hpd-cart-recommendation" href="${safeUrl(product.url)}">
              ${image ? `<img src="${safeUrl(image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="160" height="160">` : ''}
              <strong>${escapeHtml(product.title)}</strong>
              <span>${formatMoney(incPrice)} inc VAT</span>
            </a>`;
        }).join('')}</div>`;
        this.recommendations.classList.remove('is-hidden');
      } catch (error) {
        this.recommendations.classList.add('is-hidden');
      }
    }

    updateHeaderCount(count) {
      document.querySelectorAll('[data-header-cart-count]').forEach((element) => {
        element.dataset.headerCartCount = count > 0 ? String(count) : '';
        element.classList.toggle('visible', count > 0);
      });
    }

    emitCartCount(cart) {
      this.isEmittingCount = true;
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
      this.isEmittingCount = false;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  function enhancePredictiveSearchRequests() {
    if (!window.fetch || window.fetch.__hpdEnhanced) return;
    const nativeFetch = window.fetch.bind(window);
    const enhancedFetch = (input, init) => {
      if (typeof input === 'string' && input.includes('section_id=predictive-search')) {
        try {
          const url = new URL(input, window.location.origin);
          url.searchParams.set('resources[limit]', '8');
          url.searchParams.set('resources[limit_scope]', 'each');
          url.searchParams.set(
            'resources[options][fields]',
            'title,product_type,variants.title,vendor,variants.sku'
          );
          return nativeFetch(url.toString(), init);
        } catch (error) {
          return nativeFetch(input, init);
        }
      }
      return nativeFetch(input, init);
    };
    enhancedFetch.__hpdEnhanced = true;
    window.fetch = enhancedFetch;
  }

  function initialiseVoiceSearch(root = document) {
    root.querySelectorAll('[data-hpd-search-voice]').forEach((button) => {
      if (button.dataset.hpdInitialised === 'true') return;
      button.dataset.hpdInitialised = 'true';
      const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!Recognition) {
        button.hidden = true;
        return;
      }

      const input = button.closest('form')?.querySelector('[data-live-search-input]');
      if (!input) {
        button.hidden = true;
        return;
      }

      button.hidden = false;

      const recognition = new Recognition();
      recognition.lang = 'en-GB';
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.addEventListener('start', () => {
        button.classList.add('is-listening');
        button.setAttribute('aria-pressed', 'true');
      });
      recognition.addEventListener('end', () => {
        button.classList.remove('is-listening');
        button.setAttribute('aria-pressed', 'false');
      });
      recognition.addEventListener('result', (event) => {
        const transcript = event.results?.[0]?.[0]?.transcript;
        if (!transcript) return;
        input.value = transcript;
        input.focus();
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true, key: 'a' }));
      });
      button.addEventListener('click', () => {
        try {
          recognition.start();
        } catch (error) {
          recognition.stop();
        }
      });
    });
  }

 function initialiseMegaMenu(root = document) {
  root.querySelectorAll('[data-hpd-mega-menu]').forEach((menu) => {
    if (menu.dataset.hpdInitialised === 'true') return;

    menu.dataset.hpdInitialised = 'true';

    const categories = menu.querySelector('[data-hpd-mega-categories]');
    const toggle = menu.querySelector('[data-hpd-mega-toggle]');
    const megaPanel = menu.querySelector('[data-hpd-mega-panel]');
    const railItems = Array.from(
      menu.querySelectorAll('[data-hpd-mega-rail]')
    );
    const panels = Array.from(
      menu.querySelectorAll('[data-hpd-mega-category]')
    );

    /*
     * Prevent an accidental category change when the mouse travels
     * diagonally from the category rail into the selected panel.
     */
    let railHoverTimer = null;
    const railHoverDelay = 220;

    const cancelRailHover = () => {
      if (railHoverTimer === null) return;

      window.clearTimeout(railHoverTimer);
      railHoverTimer = null;
    };

    const setOpen = (open) => {
      if (!open) cancelRailHover();

      categories.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      megaPanel.setAttribute('aria-hidden', String(!open));
      megaPanel.inert = !open;
    };

    const selectPanel = (index) => {
      railItems.forEach((item) => {
        const active =
          item.dataset.hpdMegaRail === String(index);

        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        const active =
          panel.dataset.hpdMegaCategory === String(index);

        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    };

    toggle.addEventListener('click', () => {
      setOpen(!categories.classList.contains('is-open'));
    });

    categories.addEventListener('mouseenter', () => {
      setOpen(true);
    });

    categories.addEventListener('mouseleave', () => {
      cancelRailHover();

      if (!categories.contains(document.activeElement)) {
        setOpen(false);
      }
    });

    categories.addEventListener('focusout', (event) => {
      if (!categories.contains(event.relatedTarget)) {
        setOpen(false);
      }
    });

    railItems.forEach((item) => {
      const activateImmediately = () => {
        cancelRailHover();
        selectPanel(item.dataset.hpdMegaRail);
      };

      item.addEventListener('mouseenter', () => {
        cancelRailHover();

        // There is no reason to delay the already selected category.
        if (item.classList.contains('is-active')) return;

        railHoverTimer = window.setTimeout(() => {
          selectPanel(item.dataset.hpdMegaRail);
          railHoverTimer = null;
        }, railHoverDelay);
      });

      item.addEventListener('mouseleave', cancelRailHover);

      // Deliberate keyboard navigation and clicks remain immediate.
      item.addEventListener('focus', activateImmediately);
      item.addEventListener('click', activateImmediately);

      item.addEventListener('keydown', (event) => {
        const currentIndex = railItems.indexOf(item);
        let nextIndex = currentIndex;

        if (
          event.key === 'ArrowDown' ||
          event.key === 'ArrowRight'
        ) {
          nextIndex = (currentIndex + 1) % railItems.length;
        } else if (
          event.key === 'ArrowUp' ||
          event.key === 'ArrowLeft'
        ) {
          nextIndex =
            (currentIndex - 1 + railItems.length) %
            railItems.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = railItems.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        cancelRailHover();
        selectPanel(
          railItems[nextIndex].dataset.hpdMegaRail
        );
        railItems[nextIndex].focus();
      });
    });

    document.addEventListener('click', (event) => {
      if (!menu.contains(event.target)) {
        setOpen(false);
      }
    });

    menu.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      setOpen(false);
      toggle.focus();
    });
  });
}

  function initialiseMobileNavigationAccessibility() {
    const mobileNav = document.querySelector('[data-mobile-nav]');
    if (!mobileNav || mobileNav.dataset.hpdAccessibilityInitialised === 'true') return;

    mobileNav.dataset.hpdAccessibilityInitialised = 'true';
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const syncState = () => {
      const open = mobileNav.dataset.open === 'true';
      mobileNav.setAttribute('aria-hidden', String(!open));
      mobileNav.inert = !open;
      document.body.classList.toggle('hpd-mobile-nav-open', open);
    };

    if (menuToggle) {
      menuToggle.addEventListener('click', () => {
        if (mobileNav.dataset.open !== 'true') {
          mobileNav.inert = false;
          mobileNav.setAttribute('aria-hidden', 'false');
        }
      }, true);
    }

    new MutationObserver(syncState).observe(mobileNav, {
      attributes: true,
      attributeFilter: ['data-open']
    });
    syncState();
  }

  if (!customElements.get('same-day-despatch')) {
    class SameDayDespatch extends HTMLElement {
      constructor() {
        super();
        this.timeZone = this.dataset.timeZone || 'Europe/London';
        this.cutoffHour = Number.parseInt(this.dataset.cutoffHour, 10) || 16;
        this.bankHolidays = new Set(
          (this.dataset.bankHolidays || '').split(',').map((date) => date.trim()).filter(Boolean)
        );
      }

      connectedCallback() {
        this.messageElement = this.querySelector('[data-despatch-message]');
        this.timingElement = this.querySelector('[data-despatch-timing]');
        window.clearInterval(this.updateTimer);
        this.update();
        this.updateTimer = window.setInterval(() => this.update(), 15000);
      }

      disconnectedCallback() {
        window.clearInterval(this.updateTimer);
      }

      getLondonParts(date) {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: this.timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23'
        }).formatToParts(date);
        return Object.fromEntries(
          parts.filter((part) => part.type !== 'literal').map((part) => [part.type, Number.parseInt(part.value, 10)])
        );
      }

      getDateKey(parts) {
        return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
      }

      getCalendarDate(parts) {
        return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
      }

      getDateParts(date) {
        return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
      }

      isWorkingDay(parts) {
        const day = this.getCalendarDate(parts).getUTCDay();
        return day !== 0 && day !== 6 && !this.bankHolidays.has(this.getDateKey(parts));
      }

      getNextWorkingDay(parts) {
        const date = this.getCalendarDate(parts);
        do {
          date.setUTCDate(date.getUTCDate() + 1);
        } while (!this.isWorkingDay(this.getDateParts(date)));
        return this.getDateParts(date);
      }

      getTimeZoneOffset(timestamp) {
        const date = new Date(timestamp);
        const parts = this.getLondonParts(date);
        const representedAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
        return representedAsUtc - Math.floor(timestamp / 1000) * 1000;
      }

      getCutoffTimestamp(parts) {
        const guess = Date.UTC(parts.year, parts.month - 1, parts.day, this.cutoffHour, 0, 0);
        const first = guess - this.getTimeZoneOffset(guess);
        return guess - this.getTimeZoneOffset(first);
      }

      formatRemaining(milliseconds) {
        const totalMinutes = Math.max(0, Math.ceil(milliseconds / 60000));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours === 0) return `${minutes}m`;
        if (minutes === 0) return `${hours}h`;
        return `${hours}h ${minutes}m`;
      }

      formatWorkingDay(parts) {
        return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'long' }).format(this.getCalendarDate(parts));
      }

      formatCutoff() {
        if (this.cutoffHour === 12) return '12 noon';
        return this.cutoffHour < 12 ? `${this.cutoffHour}am` : `${this.cutoffHour - 12}pm`;
      }

      update() {
        if (!this.messageElement || !this.timingElement) return;
        const now = new Date();
        const nowParts = this.getLondonParts(now);
        const currentDate = { year: nowParts.year, month: nowParts.month, day: nowParts.day };
        const cutoffTimestamp = this.getCutoffTimestamp(currentDate);
        const cutoffLabel = this.formatCutoff();

        if (this.isWorkingDay(currentDate) && now.getTime() < cutoffTimestamp) {
          this.dataset.state = 'counting';
          this.messageElement.textContent = `Order by ${cutoffLabel} for same-day despatch`;
          this.timingElement.textContent = `Order within ${this.formatRemaining(cutoffTimestamp - now.getTime())}`;
        } else {
          const nextWorkingDay = this.getNextWorkingDay(currentDate);
          this.dataset.state = 'next-day';
          this.messageElement.textContent = `Next despatch day: ${this.formatWorkingDay(nextWorkingDay)}`;
          this.timingElement.textContent = `Order by ${cutoffLabel} for despatch that day`;
        }
        this.timingElement.hidden = false;
      }
    }

    customElements.define('same-day-despatch', SameDayDespatch);
  }

  function updateVatMeta(container, variant) {
    if (!container || !variant) return;
    container.querySelectorAll('[data-hpd-vat-price-meta]').forEach((meta) => {
      const taxable = variant.taxable !== false;
      const primary = meta.querySelector('[data-hpd-vat-primary-label]');
      const secondary = meta.querySelector('[data-hpd-vat-secondary]');
      const secondaryLabel = meta.querySelector('[data-hpd-vat-secondary-label]');
      const secondaryRow = meta.querySelector('.hpd-vat-price-meta__secondary');
      if (!primary || !secondary || !secondaryLabel || !secondaryRow) return;
      meta.dataset.hpdTaxable = String(taxable);
      if (!taxable) {
        primary.textContent = 'No VAT charged';
        secondaryRow.hidden = true;
        return;
      }

      secondaryRow.hidden = false;
      primary.textContent = config.taxesIncluded ? 'inc VAT' : 'ex VAT';
      secondaryLabel.textContent = config.taxesIncluded ? 'ex VAT' : 'inc VAT';
      secondary.textContent = formatMoney(
        config.taxesIncluded ? Math.round(variant.price / 1.2) : Math.round(variant.price * 1.2)
      );
    });

    container.querySelectorAll('[data-hpd-delivery-countdown]').forEach((countdown) => {
      countdown.hidden = variant.available === false;
    });
  }

  function updateStockLeadTime(container, variant) {
  if (!container || !variant?.id) return;

  const notices = container.querySelectorAll('[data-hpd-stock-notice]');
  const stockMap = container.querySelector('[data-hpd-stock-map]');

  if (!notices.length || !stockMap) return;

  try {
    if (!stockMap.hpdVariants) {
      stockMap.hpdVariants = JSON.parse(stockMap.textContent);
    }

    const selected = stockMap.hpdVariants.find(
      (item) => String(item.id) === String(variant.id)
    );

    const selectedState = selected?.state || 'none';

    notices.forEach((notice) => {
      notice.hidden =
        notice.dataset.hpdStockNotice !== selectedState;
    });
  } catch (error) {
    notices.forEach((notice) => {
      notice.hidden = true;
    });

    console.warn(
      '[HPD stock notice] Unable to read stock state.',
      error
    );
  }
}

  function bindVariantUpdates(root = document) {
    root.querySelectorAll('[data-variant-selection]').forEach((selection) => {
      if (selection.dataset.hpdVatInitialised === 'true') return;
      selection.dataset.hpdVatInitialised = 'true';
      selection.addEventListener('variant-change', (event) => {
        const container = selection.closest('[data-product-wrapper]') || selection.closest('.shopify-section') || document;
        updateVatMeta(container, event.detail?.variant);
        updateStockLeadTime(container, event.detail?.variant);
      });
    });
  }

  const paymentTermsAdjusting = new WeakSet();

  function correctPaymentTerms(terms) {
    if (!terms || paymentTermsAdjusting.has(terms)) return;
    const productRoot = terms.closest('[data-product-wrapper]') || terms.closest('.shopify-section');
    const dataElement = productRoot?.querySelector('.js-hpd-vat-payment-terms-data');
    const rawMetadata = terms.getAttribute('shopify-meta');
    if (!dataElement || !rawMetadata || config.taxesIncluded) return;

    try {
      const pricing = JSON.parse(dataElement.textContent);
      const metadata = JSON.parse(rawMetadata);
      if (!Array.isArray(metadata.variants) || !Array.isArray(pricing.variants)) return;
      const prices = new Map(pricing.variants.map((variant) => [String(variant.id), variant]));
      const parseMoney = (value) => {
        const amount = Number.parseFloat(String(value || '').replace(/[^0-9.,-]/g, '').replace(/,/g, ''));
        return Number.isFinite(amount) ? Math.round(amount * 100) : null;
      };
      const minimum = parseMoney(metadata.min_price);
      const maximum = parseMoney(metadata.max_price);
      let changed = false;

      metadata.variants.forEach((variant) => {
        const price = prices.get(String(variant.id));
        if (!price?.taxable || !Number.isFinite(price.priceIncludingVatMinor)) return;
        const fullPrice = price.priceIncludingVatMinor;
        variant.full_price = formatMoney(fullPrice);
        variant.price_per_term = formatMoney(Math.round(fullPrice / (variant.number_of_payment_terms || 3)));
        if (minimum !== null && maximum !== null) variant.eligible = fullPrice >= minimum && fullPrice <= maximum;
        changed = true;
      });

      if (!changed) return;
      paymentTermsAdjusting.add(terms);
      terms.setAttribute('shopify-meta', JSON.stringify(metadata));
      const release = () => paymentTermsAdjusting.delete(terms);
      if (window.queueMicrotask) window.queueMicrotask(release);
      else Promise.resolve().then(release);
    } catch (error) {
      console.warn('[HPD VAT] Unable to correct payment terms.', error);
    }
  }

  function initialisePaymentTerms(root = document) {
    if (root.matches?.('shopify-payment-terms')) correctPaymentTerms(root);
    root.querySelectorAll?.('shopify-payment-terms').forEach(correctPaymentTerms);
  }

  function start() {
    enhancePredictiveSearchRequests();
    initialiseVoiceSearch();
    initialiseMegaMenu();
    initialiseMobileNavigationAccessibility();
    bindVariantUpdates();
    initialisePaymentTerms();

    const shell = document.querySelector('[data-hpd-cart-shell]');
    if (shell) window.hpdCartDrawer = new HPDCartDrawer(shell);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.target.matches?.('shopify-payment-terms')) {
          correctPaymentTerms(mutation.target);
        }
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          initialiseVoiceSearch(node);
          initialiseMegaMenu(node);
          bindVariantUpdates(node);
          initialisePaymentTerms(node);
        });
      });
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['shopify-meta']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
/*
 * HPD cascading mega-menu extension.
 * Level-two links preview their child collections on hover or keyboard focus.
 * Clicking still opens the selected collection.
 */
(() => {
  'use strict';

  function initialiseHPDCascadingMenu(root = document) {
    root.querySelectorAll('[data-hpd-mega-menu]').forEach((menu) => {
      if (menu.dataset.hpdCascadeInitialised === 'true') return;

      const subRailItems = Array.from(
        menu.querySelectorAll('[data-hpd-mega-subrail]')
      );

      if (!subRailItems.length) return;

      menu.dataset.hpdCascadeInitialised = 'true';

      const selectSubPanel = (categoryPanel, key) => {
        if (!categoryPanel) return;

        categoryPanel
          .querySelectorAll('[data-hpd-mega-subrail]')
          .forEach((item) => {
            const active =
              item.dataset.hpdMegaSubrail === String(key);

            item.classList.toggle('is-active', active);
          });

        categoryPanel
          .querySelectorAll('[data-hpd-mega-subcategory]')
          .forEach((panel) => {
            const active =
              panel.dataset.hpdMegaSubcategory === String(key);

            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
          });
      };

      subRailItems.forEach((item) => {
        const categoryPanel = item.closest(
          '[data-hpd-mega-category]'
        );

        const activate = () => {
          selectSubPanel(
            categoryPanel,
            item.dataset.hpdMegaSubrail
          );
        };

        item.addEventListener('mouseenter', activate);
        item.addEventListener('focus', activate);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseHPDCascadingMenu(),
      { once: true }
    );
  } else {
    initialiseHPDCascadingMenu();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseHPDCascadingMenu(event.target);
  });
})();
